/**
 * Query-key factory for workbench_v2.
 *
 * Hierarchy mirrors the URL structure so invalidating an ancestor
 * naturally invalidates every descendant cache entry:
 *
 *   ["workbench-v2", projectId]
 *     └─ "tests"
 *          ├─ list
 *          └─ [testId]
 *               ├─ detail
 *               └─ "runs"
 *                    ├─ list
 *                    └─ [runId]
 *                         └─ detail
 */
export const workbenchV2Keys = {
  all: ["workbench-v2"] as const,
  project: (projectId: string) => [...workbenchV2Keys.all, projectId] as const,

  tests: (projectId: string) =>
    [...workbenchV2Keys.project(projectId), "tests"] as const,
  testsList: (projectId: string, requirementId?: string) =>
    [
      ...workbenchV2Keys.tests(projectId),
      "list",
      requirementId ?? null,
    ] as const,
  test: (projectId: string, testId: string) =>
    [...workbenchV2Keys.tests(projectId), testId] as const,
  testDetail: (projectId: string, testId: string) =>
    [...workbenchV2Keys.test(projectId, testId), "detail"] as const,

  runs: (projectId: string, testId: string) =>
    [...workbenchV2Keys.test(projectId, testId), "runs"] as const,
  runsList: (projectId: string, testId: string) =>
    [...workbenchV2Keys.runs(projectId, testId), "list"] as const,
  run: (projectId: string, runId: string) =>
    [...workbenchV2Keys.project(projectId), "runs", runId] as const,
  runDetail: (projectId: string, runId: string) =>
    [...workbenchV2Keys.run(projectId, runId), "detail"] as const,
  // Variable-trace lookups live under the run subtree so invalidating
  // a run (e.g. after a re-run on the same artefact) drops every
  // cached trace from the prior attempt without per-metric bookkeeping.
  trace: (projectId: string, runId: string, varName: string) =>
    [...workbenchV2Keys.run(projectId, runId), "trace", varName] as const,

  // Combined "Run all" project runs.
  projectRun: (projectId: string, projectRunId: string) =>
    [
      ...workbenchV2Keys.project(projectId),
      "project-runs",
      projectRunId,
    ] as const,
};
